import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { createRingback, createRingtone } from "@/lib/ringback";
import { toast } from "sonner";
import { Phone, PhoneSlash, VideoCamera } from "@phosphor-icons/react";

const CallCtx = createContext(null);
export const useCall = () => useContext(CallCtx);

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function CallProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const [incoming, setIncoming] = useState(null);
  const [outgoing, setOutgoing] = useState(null);
  const [groupStatus, setGroupStatus] = useState(null); // { room_code, members:[{name,status}] }

  const updateGroupMember = (who, status) => {
    if (!who) return;
    setGroupStatus((g) => {
      if (!g) return g;
      const members = g.members.map((m) => (m.name === who ? { ...m, status } : m));
      return { ...g, members };
    });
  };

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const timeoutRef = useRef(null);
  const incomingRef = useRef(null);
  const outgoingRef = useRef(null);
  const ringbackRef = useRef(null);
  const ringtoneRef = useRef(null);
  if (!ringbackRef.current) ringbackRef.current = createRingback();
  if (!ringtoneRef.current) ringtoneRef.current = createRingtone();
  const placingRef = useRef(false);

  const goRoom = (room, video, cid) =>
    navigate(`/app/meet?room=${room}&call=1&audio=${video ? "0" : "1"}${cid ? `&cid=${cid}` : ""}`);

  const clearOutgoing = () => {
    try { ringbackRef.current?.stop(); } catch {}
    clearTimeout(timeoutRef.current);
    outgoingRef.current = null;
    setOutgoing(null);
  };
  const clearIncoming = () => {
    try { ringtoneRef.current?.stop(); } catch {}
    incomingRef.current = null;
    setIncoming(null);
  };

  const startCall = async (recipient, video = true) => {
    if (outgoingRef.current || incomingRef.current) { toast.error("You're already on a call"); return; }
    if (placingRef.current) return;
    placingRef.current = true;
    try {
      const { data } = await api.post("/calls/invite", { recipient, video });
      const out = { call_id: data.call_id, room_code: data.room_code, video: data.video, to_name: data.to_name };
      outgoingRef.current = out; setOutgoing(out);
      try { ringbackRef.current?.start(); } catch {}
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => { toast(`${out.to_name} didn't answer`); cancelCall(); }, 45000);
      if (!data.online) toast(`${data.to_name} isn't online — we've sent a call notification.`);
    } catch (e) { toast.error(apiError(e)); }
    finally { placingRef.current = false; }
  };

  const startLinkCall = async (video = true) => {
    try {
      const { data } = await api.post("/calls/link", { video });
      const link = `${window.location.origin}/join?room=${data.room_code}`;
      try { await navigator.clipboard.writeText(link); toast.success("Call link copied — share it, then wait in the room"); }
      catch { toast.success("Call link created"); }
      goRoom(data.room_code, video);
    } catch (e) { toast.error(apiError(e)); }
  };

  const startGroupCall = async (recipients, video = true) => {
    if (placingRef.current) return;
    placingRef.current = true;
    try {
      const { data } = await api.post("/calls/group", { recipients, video });
      if (data.invited?.length) toast.success(`Ringing ${data.invited.length} member${data.invited.length === 1 ? "" : "s"}…`);
      if (data.unreachable?.length) toast(`Not found: ${data.unreachable.join(", ")}`);
      setGroupStatus({ room_code: data.room_code, members: (data.invited || []).map((n) => ({ name: n, status: "ringing" })) });
      qc.invalidateQueries({ queryKey: ["call-history"] });
      navigate(`/app/meet?room=${data.room_code}&call=1&audio=${video ? "0" : "1"}&cid=${data.group_id}`);
    } catch (e) { toast.error(apiError(e)); } finally { placingRef.current = false; }
  };

  const cancelCall = async () => {
    const o = outgoingRef.current;
    clearOutgoing();
    if (o) { try { await api.post(`/calls/${o.call_id}/cancel`); } catch {} }
    qc.invalidateQueries({ queryKey: ["call-history"] });
  };

  const acceptCall = async () => {
    const i = incomingRef.current;
    if (!i) return;
    clearIncoming();
    try { await api.post(`/calls/${i.call_id}/accept`); } catch {}
    qc.invalidateQueries({ queryKey: ["call-history"] });
    goRoom(i.room_code, i.video);
  };

  const declineCall = async () => {
    const i = incomingRef.current;
    clearIncoming();
    if (i) { try { await api.post(`/calls/${i.call_id}/decline`); } catch {} }
    qc.invalidateQueries({ queryKey: ["call-history"] });
  };

  const declineWithCallback = async () => {
    const i = incomingRef.current;
    clearIncoming();
    if (i) {
      try { await api.post(`/calls/${i.call_id}/decline`); } catch {}
      try { await api.post(`/calls/${i.call_id}/callback`, { text: "" }); toast.success("Sent: \u201cI'll call back shortly\u201d"); } catch {}
      qc.invalidateQueries({ queryKey: ["call-history"] });
    }
  };

  // ---- signaling websocket
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const connect = () => {
      const token = localStorage.getItem("nexus_token");
      if (!token) return;
      const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
      const ws = new WebSocket(`${base}/api/ws/calls?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        let m; try { m = JSON.parse(ev.data); } catch { return; }
        switch (m.type) {
          case "incoming-call": {
            if (outgoingRef.current || incomingRef.current) { api.post(`/calls/${m.call_id}/decline`).catch(() => {}); break; }
            const inc = { call_id: m.call_id, room_code: m.room_code, video: m.video, from_name: m.from_name };
            incomingRef.current = inc; setIncoming(inc);
            try { ringtoneRef.current?.start(); } catch {}
            break;
          }
          case "call-accepted": {
            const o = outgoingRef.current;
            qc.invalidateQueries({ queryKey: ["call-history"] });
            if (m.group) { toast.success(`${m.who || "A member"} joined`); updateGroupMember(m.who, "joined"); }
            else { clearOutgoing(); goRoom(m.room_code, m.video, o?.call_id); }
            break;
          }
          case "call-declined": {
            const o = outgoingRef.current;
            qc.invalidateQueries({ queryKey: ["call-history"] });
            if (m.group) { toast(`${m.who || "A member"} can't join`); updateGroupMember(m.who, "declined"); }
            else { clearOutgoing(); toast(`${o?.to_name || "They"} declined the call`); }
            break;
          }
          case "call-canceled": clearIncoming(); qc.invalidateQueries({ queryKey: ["call-history"] }); break;
          case "call-message": { toast(`${m.from}: ${m.text}`); qc.invalidateQueries({ queryKey: ["call-history"] }); break; }
          case "chat-message": {
            qc.invalidateQueries({ queryKey: ["chat-threads"] });
            qc.invalidateQueries({ queryKey: ["chat-messages"] });
            window.dispatchEvent(new CustomEvent("iyeneh-chat", { detail: m }));
            const msg = m.message || {};
            const onThread = location.pathname === "/app/text" && new URLSearchParams(location.search).get("with") === msg.from_user_id;
            if (!onThread) { try { ringtoneRef.current; } catch {} toast(`${msg.from_name}: ${msg.text || "\uD83D\uDCCE media"}`); }
            break;
          }
          case "chat-read":
          case "chat-typing":
          case "chat-reaction":
          case "chat-settings": window.dispatchEvent(new CustomEvent("iyeneh-chat", { detail: m })); qc.invalidateQueries({ queryKey: ["chat-messages"] }); break;
          default: break;
        }
      };
      ws.onclose = () => { if (!stopped) reconnectRef.current = setTimeout(connect, 3000); };
    };
    connect();
    return () => { stopped = true; clearTimeout(reconnectRef.current); try { wsRef.current?.close(); } catch {} };
  }, [user?.id]); // eslint-disable-line

  // ---- web push subscription (rings even when the app is closed)
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return;
        const { data } = await api.get("/push/vapid-public-key");
        if (!data.key) return;
        if (Notification.permission === "denied") return;
        if (Notification.permission === "default") {
          const p = await Notification.requestPermission();
          if (p !== "granted") return;
        }
        const reg = await navigator.serviceWorker.ready;
        let sub = await reg.pushManager.getSubscription();
        if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.key) });
        await api.post("/push/subscribe", { subscription: sub.toJSON() });
      } catch { /* best-effort */ }
    })();
  }, [user?.id]); // eslint-disable-line

  // ---- open from a push notification: /app/call?incoming=<id>&room=<code>&answer=1  OR  ?callback=<uid>&video=1
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const cidParam = sp.get("incoming");
    const callbackUid = sp.get("callback");
    if (location.pathname === "/app/call" && callbackUid) {
      startCall(callbackUid, sp.get("video") === "1");
      navigate("/app/call", { replace: true });
      return;
    }
    if (location.pathname === "/app/call" && cidParam) {
      const autoAnswer = sp.get("answer") === "1";
      api.get(`/calls/${cidParam}`).then(({ data }) => {
        if (data.status === "ringing" && !incomingRef.current) {
          const inc = { call_id: cidParam, room_code: data.room_code, video: data.video, from_name: data.from_name };
          incomingRef.current = inc; setIncoming(inc);
          try { ringtoneRef.current?.start(); } catch {}
          if (autoAnswer) acceptCall();
        }
      }).catch(() => {});
      navigate("/app/call", { replace: true });
    }
  }, [location.pathname, location.search]); // eslint-disable-line

  // ---- clear the group-ring panel once you leave the meeting room
  useEffect(() => {
    if (groupStatus && location.pathname !== "/app/meet") setGroupStatus(null);
  }, [location.pathname]); // eslint-disable-line

  // ---- service worker click → focus + navigate
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMsg = (e) => { if (e.data?.type === "open-url" && e.data.url) navigate(e.data.url); };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => navigator.serviceWorker.removeEventListener("message", onMsg);
  }, []); // eslint-disable-line

  return (
    <CallCtx.Provider value={{ startCall, startGroupCall, startLinkCall, acceptCall, declineCall, declineWithCallback, cancelCall, incoming, outgoing, groupStatus, setGroupStatus }}>
      {children}

      {incoming && (
        <div className="fixed inset-x-0 top-4 z-[70] mx-auto w-full max-w-sm rounded-2xl border border-primary bg-card p-4 shadow-2xl" data-testid="incoming-call-popup">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Incoming {incoming.video ? "video" : "audio"} call</p>
          <p className="mt-1 flex items-center gap-2 font-display text-lg font-semibold">
            {incoming.video ? <VideoCamera size={20} weight="fill" className="text-primary" /> : <Phone size={20} weight="fill" className="text-primary" />}
            {incoming.from_name}
          </p>
          <div className="mt-3 flex gap-3">
            <button data-testid="incoming-accept" onClick={acceptCall} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-emerald-500 py-2.5 font-semibold text-white transition-transform hover:scale-[1.02]"><Phone size={18} weight="fill" /> Accept</button>
            <button data-testid="incoming-decline" onClick={declineCall} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-destructive py-2.5 font-semibold text-white transition-transform hover:scale-[1.02]"><PhoneSlash size={18} weight="fill" /> Decline</button>
          </div>
          <button data-testid="incoming-callback" onClick={declineWithCallback} className="mt-2 w-full rounded-full border border-border py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-primary hover:text-foreground">Can't talk — I'll call back</button>
        </div>
      )}

      {outgoing && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" data-testid="outgoing-call-popup">
          <div className="w-full max-w-xs rounded-3xl border border-white/10 bg-[#08111F] p-8 text-center text-white">
            <div className="mx-auto flex h-16 w-16 animate-pulse items-center justify-center rounded-full bg-primary">
              {outgoing.video ? <VideoCamera size={30} weight="fill" /> : <Phone size={30} weight="fill" />}
            </div>
            <p className="mt-5 text-xs uppercase tracking-[0.2em] text-white/50">Calling…</p>
            <p className="mt-1 font-display text-xl font-semibold">{outgoing.to_name}</p>
            <p className="mt-1 text-sm text-white/50">{outgoing.video ? "Video call" : "Audio call"} over the internet</p>
            <button data-testid="outgoing-cancel" onClick={cancelCall} className="mt-6 inline-flex items-center gap-2 rounded-full bg-destructive px-6 py-3 font-semibold text-white transition-transform hover:scale-[1.02]"><PhoneSlash size={18} weight="fill" /> Cancel</button>
          </div>
        </div>
      )}
    </CallCtx.Provider>
  );
}
